import { alpha } from '@mui/material';
import {
  colors as themeColors,
  brand,
  error as errorShades,
  greyLightMode,
} from '@/theme/colors';

export const colors = {
  primary: themeColors.primary.main,
  primaryLight: themeColors.primary.light,
  secondary: themeColors.secondary.main,
  error: errorShades.shade7,
  gray50: greyLightMode.shade2,
  gray100: greyLightMode.shade3,
  gray200: greyLightMode.shade4,
  gray300: greyLightMode.shade5,
  gray400: greyLightMode.shade6,
  gray500: greyLightMode.shade7,
  gray600: greyLightMode.shade8,
  gray700: greyLightMode.shade9,
  gray800: greyLightMode.shade10,
};

export { brand, greyLightMode };

// ── NotificationItem styles ──

export const notificationItemStyles = {
  listItem: {
    py: 1.5,
    px: 2,
    gap: 1.5,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      bgcolor: colors.gray50,
      transform: 'translateX(2px)',
    },
  },
  listItemAvatar: { minWidth: 'auto' },
  avatar: {
    width: 40,
    height: 40,
    bgcolor: colors.primary,
    border: `1px solid ${colors.gray200}`,
  },
  titleText: {
    color: colors.gray800,
    fontSize: '0.85rem',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  timeText: {
    color: colors.gray400,
    fontSize: '0.7rem',
    flexShrink: 0,
  },
  bodyText: {
    color: colors.gray500,
    fontSize: '0.75rem',
    lineHeight: 1.5,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  companyChip: {
    mt: 0.75,
    height: 18,
    fontSize: '0.65rem',
    fontWeight: 600,
    bgcolor: alpha(colors.primary, 0.1),
    color: colors.primary,
    '& .MuiChip-label': { px: 0.75 },
  },
};

// ── NotificationDialog styles ──

export const notificationDialogStyles = {
  paper: {
    position: 'fixed',
    top: 70,
    right: 20,
    m: 0,
    width: 360,
    maxWidth: '90vw',
    maxHeight: 'calc(100vh - 100px)',
    borderRadius: 2,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    border: `1px solid ${colors.gray200}`,
  },
  backdrop: { backgroundColor: 'transparent' },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    py: 1.5,
    px: 2,
    bgcolor: 'white',
    borderBottom: `1px solid ${colors.gray200}`,
  },
  headerAvatar: {
    width: 32,
    height: 32,
    bgcolor: colors.primary,
  },
  headerTitle: { color: colors.gray800, fontSize: '0.85rem' },
  headerCount: { color: colors.gray500, fontSize: '0.7rem' },
  closeButton: {
    color: colors.gray500,
    '&:hover': { bgcolor: colors.gray100 },
  },
  content: { p: 0, maxHeight: 350, overflow: 'auto', bgcolor: 'white' },
  divider: { borderColor: colors.gray100 },
  // Dialog's inline NotificationItem (slightly smaller)
  dialogListItem: {
    py: 1.25,
    px: 2,
    gap: 1.5,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': { bgcolor: colors.gray50 },
  },
  dialogAvatar: {
    width: 36,
    height: 36,
    bgcolor: colors.primary,
    border: `1px solid ${colors.gray200}`,
  },
  dialogTitleText: {
    color: colors.gray800,
    fontSize: '0.8rem',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dialogTimeText: {
    color: colors.gray400,
    fontSize: '0.65rem',
    flexShrink: 0,
  },
  dialogBodyText: {
    color: colors.gray500,
    fontSize: '0.7rem',
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    mt: 0.25,
  },
  // Dialog skeleton
  dialogSkeletonCircle: {
    background: `linear-gradient(90deg, ${colors.gray200} 25%, ${colors.gray100} 50%, ${colors.gray200} 75%)`,
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    '@keyframes shimmer': {
      '0%': { backgroundPosition: '200% 0' },
      '100%': { backgroundPosition: '-200% 0' },
    },
  },
  dialogSkeletonListItem: { py: 1.25, px: 2, gap: 1.5 },
  // Dialog error state
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    py: 4,
    px: 2,
  },
  errorAvatar: {
    width: 40,
    height: 40,
    bgcolor: alpha(colors.error, 0.1),
    mb: 1.5,
  },
  errorTitle: { color: colors.gray800, fontSize: '0.8rem', mb: 0.25 },
  errorSubtitle: { color: colors.gray500, fontSize: '0.7rem', textAlign: 'center' },
  // Dialog empty state
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    py: 5,
    px: 2,
  },
  emptyAvatar: {
    width: 48,
    height: 48,
    bgcolor: colors.gray100,
    mb: 1.5,
  },
  emptyTitle: { color: colors.gray800, fontSize: '0.8rem', mb: 0.25 },
  emptySubtitle: { color: colors.gray500, fontSize: '0.7rem', textAlign: 'center', maxWidth: 200 },
  // Footer
  footer: {
    p: 1.5,
    borderTop: `1px solid ${colors.gray200}`,
    bgcolor: colors.gray50,
  },
  viewAllButton: {
    py: 0.75,
    borderRadius: 1.5,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8rem',
    color: colors.primary,
    '&:hover': { bgcolor: alpha(colors.primary, 0.08) },
  },
};

// ── NotificationsEmptyState styles ──

export const notificationsEmptyStateStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    py: 6,
    px: 3,
  },
  avatar: {
    width: 56,
    height: 56,
    bgcolor: colors.gray100,
    mb: 2,
  },
  title: { color: colors.gray800, fontSize: '0.85rem', mb: 0.5 },
  subtitle: { color: colors.gray500, fontSize: '0.75rem', textAlign: 'center', maxWidth: 280 },
};

// ── NotificationsErrorState styles ──

export const notificationsErrorStateStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    py: 6,
    px: 3,
  },
  avatar: {
    width: 48,
    height: 48,
    bgcolor: alpha(colors.error, 0.1),
    mb: 2,
  },
  title: { color: colors.gray800, fontSize: '0.85rem', mb: 0.5 },
  subtitle: { color: colors.gray500, fontSize: '0.75rem', textAlign: 'center' },
};

// ── NotificationsSkeleton styles ──

export const notificationsSkeletonStyles = {
  listItem: { py: 1.5, px: 2, gap: 1.5 },
  circle: {
    background: `linear-gradient(90deg, ${colors.gray200} 25%, ${colors.gray100} 50%, ${colors.gray200} 75%)`,
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    '@keyframes shimmer': {
      '0%': { backgroundPosition: '200% 0' },
      '100%': { backgroundPosition: '-200% 0' },
    },
  },
};

// ── NotificationsPage styles ──

export const notificationsPageStyles = {
  container: { p: { xs: 2, md: 3 }, bgcolor: colors.gray50, minHeight: '100vh' },
  headerAvatar: {
    width: 36,
    height: 36,
    bgcolor: colors.primary,
  },
  headerTitle: { color: colors.gray800, fontSize: '1rem' },
  headerCount: { color: colors.gray500, fontSize: '0.75rem' },
  refreshButton: {
    bgcolor: colors.gray100,
    '&:hover': { bgcolor: colors.gray200 },
    '&.Mui-disabled': { bgcolor: colors.gray100 },
  },
  refreshIcon: (isFetching: boolean) => ({
    fontSize: 18,
    color: colors.gray600,
    animation: isFetching ? 'spin 1s linear infinite' : 'none',
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
  }),
  card: {
    borderRadius: 2,
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
    border: `1px solid ${colors.gray200}`,
    overflow: 'hidden',
  },
  divider: { borderColor: colors.gray100 },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    py: 1.5,
    borderTop: `1px solid ${colors.gray200}`,
    bgcolor: colors.gray50,
  },
  pagination: {
    '& .MuiPaginationItem-root': {
      fontSize: '0.75rem',
      minWidth: 28,
      height: 28,
      color: colors.gray600,
      '&.Mui-selected': {
        bgcolor: colors.primary,
        color: 'white',
        '&:hover': { bgcolor: colors.primaryLight },
      },
      '&:hover': { bgcolor: colors.gray100 },
    },
  },
};
